import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { authAPI, getAccessToken, clearTokens, setAuthInvalidHandler } from '../services/api';

const AuthContext = createContext(null);

// Role definitions
export const ROLES = {
  ADMIN: 'admin',
  COMPANY_ADMIN: 'company_admin',
  MANAGER: 'manager',
  USER: 'user',
};

// LocalStorage key for persisting selected company
const STORAGE_KEY_COMPANY = 'statusreport_active_company';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    setAuthInvalidHandler(() => {
      setUser(null);
      setIsAuthenticated(false);
    });

    const checkAuth = async () => {
      const token = getAccessToken();
      if (token) {
        try {
          const userData = await authAPI.getMe();
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          clearTokens();
        }
      }
      setIsLoading(false);
    };

    checkAuth();

    return () => {
      setAuthInvalidHandler(null);
    };
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    const userData = await authAPI.login(email, password);
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  }, []);

  // Logout
  const logout = useCallback(async () => {
    await authAPI.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Role check
  const hasRole = useCallback((requiredRole) => {
    if (!user) return false;

    const roleRank = {
      [ROLES.USER]: 1,
      [ROLES.MANAGER]: 2,
      [ROLES.COMPANY_ADMIN]: 3,
      [ROLES.ADMIN]: 4,
    };

    const currentRank = roleRank[user.role] || 0;
    const requiredRank = roleRank[requiredRole] || 0;
    return currentRank >= requiredRank;
  }, [user]);

  // Multiple role check
  const hasAnyRole = useCallback((roles) => {
    return roles.some((role) => hasRole(role));
  }, [hasRole]);

  // Switch active company
  const switchCompany = useCallback(async (companyId) => {
    try {
      await authAPI.switchCompany(companyId);
      localStorage.setItem(STORAGE_KEY_COMPANY, companyId);
      // Refetch user data to get updated state with new active company
      const userData = await authAPI.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Switch company failed:', error);
      throw error;
    }
  }, []);

  // Update device name
  const updateDeviceName = useCallback(async (newDeviceName) => {
    try {
      await authAPI.updateDeviceName(newDeviceName);
      setUser((prev) => ({
        ...prev,
        device_name: newDeviceName,
      }));
    } catch (error) {
      console.error('Update device name failed:', error);
      throw error;
    }
  }, []);

  // Get active company
  const getActiveCompany = useCallback(() => {
    if (!user?.companies || !user?.activeCompanyId) return null;
    return user.companies.find((c) => c.slug === user.activeCompanyId || c.id === user.activeCompanyId) || null;
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      setUser,
      isAuthenticated,
      isLoading,
      login,
      logout,
      hasRole,
      hasAnyRole,
      switchCompany,
      updateDeviceName,
      getActiveCompany,
      ROLES,
    }),
    [user, isAuthenticated, isLoading, login, logout, hasRole, hasAnyRole, switchCompany, updateDeviceName, getActiveCompany]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
